import { useMemo } from 'react';
import produce from 'immer';
import ms from 'ms';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useAuth } from '@context/auth/base';
import type { LikeStatusesQueryVariables } from '@graphql/reactQuery';
import { LikeStatusesDocument, useQuery } from '@graphql/reactQuery';
import { createBatchingInputStore } from '@store/batchInput';

/*#__PURE__*/ gql(/* GraphQL */ `
  query LikeStatuses($input: [IsLikedInput!]!) {
    likeStatuses(input: $input) {
      entityId
      isLiked
    }
  }
`);

const { useBatchedKey } = createBatchingInputStore({
  chunkLimit: 50,
});

/**
 * This hook is responsible for getting like button status
 * you can provide a callback to set the status
 */
export const useLikeStatus = ({ id }: { id: string | undefined }) => {
  const { authUser } = useAuth();

  const authUserId = authUser?.id;

  const ids = useBatchedKey({
    key: id,
  });

  const variables = useMemo<LikeStatusesQueryVariables | false>(() => {
    if (!ids) return false;

    return {
      input: ids.map(id => ({ id })),
    };
  }, [ids]);

  const { refetch, data, setQueryData } = useQuery(LikeStatusesDocument, {
    variables: !!authUserId && variables,
    filterQueryKey: {
      authUserId,
    },
    keepPreviousData: true,
    staleTime: ms('15 seconds'),
  });

  const setLiked = useStableCallback((isLiked: boolean) => {
    if (!id) return;

    setQueryData(existing => {
      if (!existing) {
        return {
          data: {
            likeStatuses: [
              {
                entityId: id,
                isLiked,
              },
            ],
          },
        };
      }

      return produce(existing, draft => {
        for (const existingValue of draft.data.likeStatuses) {
          if (existingValue.entityId === id) {
            existingValue.isLiked = isLiked;
            return;
          }
        }
        draft.data.likeStatuses.push({
          entityId: id,
          isLiked,
        });
      });
    });
  });

  const isLiked = useMemo(
    () =>
      id ? data?.data.likeStatuses.find(value => value.entityId === id)?.isLiked ?? false : false,
    [data, id],
  );

  return { refetch, isLiked, setLiked };
};
